import { destroyCanvas } from "@/libs/canvas"
import { resizeCanvas } from "@/libs/canvas/canvas"
import type { FramePixels } from "@/libs/cv"
import { createProgram } from "@/libs/gl/program"
import { createTexture } from "@/libs/gl/texture"
import { fit, type ImageSource } from "@/libs/image"
import { retryAsync } from "@/libs/lodash"
import { cancelVideoFrameCallback, requestVideoFrameCallback } from "./video"

/** Utility class that performs HTMLVideoElement's video resize to the specified size using canvas and WebGL2 */
export class VideoResizer {
  #root: HTMLElement
  #video: HTMLVideoElement
  #resizedVideo: HTMLVideoElement | null = null
  #stream: MediaStream | null = null
  #canvas = document.createElement("canvas")

  #resolution: number
  #onReady: () => void
  #onStop: () => void

  #gl: WebGL2RenderingContext | null = null
  #vao: WebGLVertexArrayObject | null = null
  #program: WebGLProgram | null = null
  #texture: WebGLTexture | null = null

  #requestFrame: (cb: FrameRequestCallback) => void
  #frameId: ReturnType<
    typeof requestAnimationFrame | HTMLVideoElement["requestVideoFrameCallback"]
  > | null = null

  #width = 0
  #height = 0

  #isReady = false

  // Class names to visually hide the elements
  // #CLASS_NAMES = "absolute top-0 left-0 pointer-events-none".split(" ")
  #CLASS_NAMES = "absolute top-0 left-0 opacity-0 pointer-events-none".split(" ")
  #FRAME_REQUEST_RATE = 30

  #vShader = `#version 300 es
    in vec2 a_position;
    in vec2 a_texCoord;
    out vec2 v_texCoord;

    void main() {
      v_texCoord = a_texCoord;
      gl_Position = vec4(a_position, 0.0, 1.0);
    }
  `
  #fShader = `#version 300 es
    precision mediump float;

    in vec2 v_texCoord;
    out vec4 outColor;

    uniform sampler2D u_texture;

    void main() {
      outColor = texture(u_texture, v_texCoord);
    }
  `

  constructor({
    root,
    video,
    resolution,
    onReady,
    onStop,
  }: {
    /** Root element which will be used as a parent for utility's internal canvas and video elements. They are required to be appended to the DOM to work properly */
    root: HTMLElement
    video: HTMLVideoElement
    resolution: number
    onReady: () => void
    onStop: () => void
  }) {
    this.#root = root
    this.#video = video
    this.#resolution = resolution
    this.#onReady = onReady
    this.#onStop = onStop
    this.#requestFrame = this.#createRequestFrame()

    this.#init()
  }

  /** Returns video element with the resized video */
  getVideo(): ImageSource | null {
    return this.#isReady ? this.#resizedVideo : null
  }
  /** Synchronously returns frame pixels of the resized video */
  getPixels(): FramePixels | null {
    if (!this.#isReady || !this.#gl || this.#gl.isContextLost()) return null

    this.#gl.finish()

    // Pixels are read from bottom-left corner, resulting in flipped image on Y axis
    const flipped = new Uint8Array(this.#width * this.#height * 4)

    this.#gl.readPixels(
      0,
      0,
      this.#width,
      this.#height,
      this.#gl.RGBA,
      this.#gl.UNSIGNED_BYTE,
      flipped,
    )

    // Flip the pixels vertically
    const pixels = new Uint8Array(this.#width * this.#height * 4)
    const rowSize = this.#width * 4

    for (let y = 0; y < this.#height; y++) {
      const srcY = this.#height - y - 1
      pixels.set(flipped.subarray(srcY * rowSize, (srcY + 1) * rowSize), y * rowSize)
    }

    return { width: this.#width, height: this.#height, data: pixels, format: "RGBA" }
  }
  destroy() {
    this.#stopVideo()
    destroyCanvas(this.#canvas)
    this.#canvas.remove()
  }

  #stopVideo() {
    this.#destroyGL()

    this.#stream?.getVideoTracks().map((t) => t.stop())

    if (this.#resizedVideo) {
      this.#resizedVideo.srcObject = null
      this.#resizedVideo.remove()
      this.#resizedVideo = null
    }

    this.#onStop()
  }

  #destroyGL() {
    if (this.#gl && !this.#gl.isContextLost()) {
      this.#gl.deleteTexture(this.#texture)
      this.#gl.deleteProgram(this.#program)
      this.#gl.getExtension("WEBGL_lose_context")?.loseContext()
    }

    this.#gl = null
    this.#texture = null
    this.#program = null

    if (this.#frameId) cancelVideoFrameCallback(this.#video, this.#frameId)
  }

  #createRequestFrame() {
    const requestFrame = requestVideoFrameCallback.bind(null, this.#video)

    const interval = 1000 / this.#FRAME_REQUEST_RATE
    const epsilon = 0.1 * interval

    let then = 0
    let now = 0

    return (cb: FrameRequestCallback) => {
      this.#frameId = requestFrame((time) => {
        this.#frameId = null
        now = time

        now - then < interval - epsilon ? this.#requestFrame(cb) : cb((then = now))
      })
    }
  }

  #init() {
    const { width: w, height: h } = fit(
      { width: this.#video.videoWidth, height: this.#video.videoHeight },
      this.#resolution,
    )

    this.#resizedVideo = document.createElement("video")
    this.#resizedVideo.width = this.#width = w
    this.#resizedVideo.height = this.#height = h
    this.#resizedVideo.muted = true
    this.#resizedVideo.autoplay = true
    this.#resizedVideo.playsInline = true
    this.#resizedVideo.controls = false
    this.#resizedVideo.classList.add(...this.#CLASS_NAMES)
    this.#resizedVideo.addEventListener("play", () => {
      this.#isReady = true
      this.#onReady()
    })
    this.#resizedVideo.addEventListener("ended", () => {
      if (!this.#isReady) return

      this.#isReady = false
      this.#onStop()
    })
    this.#resizedVideo.srcObject = this.#stream = this.#canvas.captureStream(
      this.#FRAME_REQUEST_RATE,
    )

    this.#root.appendChild(this.#canvas)
    this.#root.appendChild(this.#resizedVideo)

    this.#canvas.addEventListener("contextlost", () => this.#destroyGL())
    this.#canvas.classList.add(...this.#CLASS_NAMES)
    this.#canvas.width = this.#width
    this.#canvas.height = this.#height

    const gl = this.#getGL()
    resizeCanvas({
      canvas: this.#canvas,
      gl,
      width: this.#width,
      height: this.#height,
      ignoreDevicePixelRatio: true,
    })

    this.#program = createProgram(gl, this.#vShader, this.#fShader)

    const positions = new Float32Array([-1, -1, 0, 1, 1, -1, 1, 1, -1, 1, 0, 0, 1, 1, 1, 0])
    this.#vao = gl.createVertexArray()
    gl.bindVertexArray(this.#vao)

    const buffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW)

    const a_position = gl.getAttribLocation(this.#program, "a_position")
    gl.enableVertexAttribArray(a_position)
    gl.vertexAttribPointer(a_position, 2, gl.FLOAT, false, 16, 0)

    const a_texCoord = gl.getAttribLocation(this.#program, "a_texCoord")
    gl.enableVertexAttribArray(a_texCoord)
    gl.vertexAttribPointer(a_texCoord, 2, gl.FLOAT, false, 16, 8)

    this.#render()
  }

  #getGL() {
    if (this.#gl) return this.#gl

    const ctx = this.#canvas?.getContext("webgl2", { preserveDrawingBuffer: true })

    if (!ctx) throw new Error("WebGL2 not supported")

    return (this.#gl = ctx)
  }

  #performResize() {
    const gl = this.#getGL()

    gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT)

    this.#texture ??= createTexture(gl, { filtering: gl.NEAREST })

    gl.bindTexture(gl.TEXTURE_2D, this.#texture)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, this.#video)

    gl.useProgram(this.#program)
    gl.bindVertexArray(this.#vao)
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
  }

  #render() {
    this.#requestFrame(() =>
      retryAsync(() => (this.#performResize(), this.#render()), 3, {
        shouldThrowEarly: () => Boolean(this.#gl && !this.#gl.isContextLost()),
        onRetry: () => {
          if (this.#gl?.isContextLost()) this.#destroyGL()
        },
        delay: 1000,
      }),
    )
  }
}
